const VIRTUAL_CAMERA_KEYWORDS = [
  'OBS',
  'Virtual Camera',
  'Virtual Webcam',
  'ManyCam',
  'Snap Camera',
  'YouCam',
  'CyberLink',
  'XSplit',
  'VCam',
  'EOS Webcam Utility',
  'DroidCam',
  'Iriun',
  'EpocCam',
];

/**
 * 判断一个媒体设备是否是常见的虚拟摄像头。
 *
 * @param {MediaDeviceInfo | { label: string }} device
 * @returns {boolean}
 */
function isVirtualCamera(device) {
  if (!device || typeof device.label !== 'string') {
    return false;
  }
  const label = device.label.toLowerCase();
  return VIRTUAL_CAMERA_KEYWORDS.some((keyword) =>
    label.includes(keyword.toLowerCase()),
  );
}

class Webcam {
  constructor(width, height, flip, option) {
    this.webcam = null;
    this.canvas = null;
    this.width = width || 640;
    this.height = height || 480;
    this.flip = flip || false;
    this.stream = null;
    this.option = option || {
      width: this.width,
      height: this.height,
      facingMode: 'user', // or 'user' for front camera
    };
  }
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async getMediaStream() {
    let devices = await navigator.mediaDevices.enumerateDevices();
    console.log('devices', devices);
    const deviceIds = devices
      .filter((item) => item.kind === 'videoinput' && !isVirtualCamera(item))
      .map((item) => item.deviceId);

    if (deviceIds.length === 0) {
      throw new Error(
        '未检测到可用摄像头，请关闭虚拟摄像头软件或连接真实摄像头后重试',
      );
    }

    for (let id = 0; id < deviceIds.length; id++) {
      let options = {
        width: { ideal: this.width },
        height: { ideal: this.height },
        ...this.option,
        deviceId: {
          ideal: deviceIds[id],
        },
        frameRate: 24,
      };

      try {
        let stream = await navigator.mediaDevices.getUserMedia({
          video: options,
        });
        return stream;
      } catch (e) {
        await this.wait(2000);
        if (id === deviceIds.length - 1) {
          throw e;
        }
      }
    }
  }

  async getWebcam() {
    this.stream = await this.getMediaStream();
    let video = document.createElement('video');
    video.srcObject = this.stream;
    video.width = this.width;
    video.height = this.height;

    return video;
  }

  get videoMateData() {
    const videoTrack = this.stream.getVideoTracks()[0];
    const capabilities = videoTrack.getCapabilities();
    if (!capabilities) {
      return null;
    }
    const cameraWidth = Object.values(capabilities.width); // 摄像头实际宽度
    const cameraHeight = Object.values(capabilities.height); // 摄像头实际高度
    return {
      width: Math.max(...cameraWidth),
      height: Math.max(...cameraHeight),
    };
  }

  async setup() {
    this.stop();
    this.webcam = await this.getWebcam();

    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvas.width = this.width;
      this.canvas.height = this.height;
    }
  }

  async stop() {
    try {
      this.stream && this.stream.getTracks().forEach((track) => track.stop());
    } catch {
      // ignore
    } finally {
      this.stream = null;
    }

    try {
      if (this.webcam) {
        this.webcam.pause();
        this.webcam.srcObject = null;
      }
    } catch {
      // ignore
    } finally {
      this.webcam = null;
    }

    this.canvas = null;
  }

  async play() {
    return this.webcam.play();
  }

  update() {
    this.renderCameraToCanvas();
  }

  renderCameraToCanvas() {
    if (this.webcam && this.canvas) {
      let ctx = this.canvas.getContext('2d');

      if (this.webcam.videoWidth !== 0) {
        const croppedCanvas = this.cropTo(this.webcam, this.width, this.flip);
        ctx.drawImage(croppedCanvas, 0, 0);
      }
    }
  }

  newCanvas = () => document.createElement('canvas');

  cropTo(image, size, flipped = false, canvas = this.newCanvas()) {
    // image image, bitmap, or canvas
    let width = image.width;
    let height = image.height;

    // if video element
    if (image instanceof HTMLVideoElement) {
      width = image.videoWidth;
      height = image.videoHeight;
    }

    const min = Math.min(width, height);
    const scale = size / min;
    const scaledW = Math.ceil(width * scale);
    const scaledH = Math.ceil(height * scale);
    const dx = scaledW - size;
    const dy = scaledH - size;
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, ~~(dx / 2) * -1, ~~(dy / 2) * -1, scaledW, scaledH);

    // canvas is already sized and cropped to center correctly
    if (flipped) {
      ctx.scale(-1, 1);
      ctx.drawImage(canvas, size * -1, 0);
    }

    return canvas;
  }
}

const DEFAULT_VIDEO_CONSTRAINTS = {
  width: 640,
  height: 480,
  facingMode: 'user',
};

/**
 * 请求摄像头媒体流，支持虚拟摄像头过滤与多设备回退。
 *
 * 实现策略：
 * 1. 先用默认约束尝试获取流（可触发权限申请，使设备 label 可用）。
 *    若成功且流来自虚拟摄像头，则停止该流并继续枚举真实设备。
 * 2. 枚举 videoinput 设备，过滤 OBS / Virtual Camera 等虚拟摄像头，
 *    逐个以 deviceId: { ideal: deviceId } 尝试获取流。
 * 3. 若仅剩虚拟摄像头/无真实摄像头，抛出可识别错误。
 *
 * @param {MediaStreamConstraints} [preferredConstraints] - 优先使用的约束，默认 640x480
 * @param {MediaStream | null} [oldStream] - 需要在此之前释放的旧流
 * @returns {Promise<MediaStream>}
 */
export async function getCameraStream(
  preferredConstraints = { video: DEFAULT_VIDEO_CONSTRAINTS },
  oldStream = null,
) {
  if (oldStream) {
    oldStream.getTracks().forEach((track) => track.stop());
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('浏览器不支持访问摄像头');
  }

  const requestStream = (constraints) =>
    navigator.mediaDevices.getUserMedia(constraints);

  const videoConstraint = preferredConstraints?.video;

  const buildConstraints = (deviceId) => ({
    ...preferredConstraints,
    video: {
      ...(typeof videoConstraint === 'object' ? videoConstraint : {}),
      deviceId: { ideal: deviceId },
    },
  });

  let defaultErr;

  // 1. 先尝试默认约束，同时触发权限申请以获取设备 label。
  try {
    const stream = await requestStream(preferredConstraints);
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack && isVirtualCamera({ label: videoTrack.label })) {
      videoTrack.stop();
    } else {
      return stream;
    }
  } catch (err) {
    defaultErr = err;
  }

  // 2. 枚举真实摄像头并逐个回退尝试。
  let allDevices = [];
  try {
    allDevices = await navigator.mediaDevices.enumerateDevices();
  } catch {
    // 枚举失败时回退到默认错误。
    throw defaultErr;
  }

  const realDevices = allDevices.filter(
    (device) => device.kind === 'videoinput' && !isVirtualCamera(device),
  );

  if (realDevices.length > 0) {
    let lastErr;
    for (let i = 0; i < realDevices.length; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const stream = await requestStream(
          buildConstraints(realDevices[i].deviceId),
        );
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack && isVirtualCamera({ label: videoTrack.label })) {
          videoTrack.stop();
          continue;
        }
        return stream;
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr;
  }

  // 3. 存在视频设备但全部为虚拟摄像头，或没有任何视频设备。
  const hasVideoDevice = allDevices.some(
    (device) => device.kind === 'videoinput',
  );
  if (hasVideoDevice) {
    throw new Error(
      '未检测到可用摄像头，请关闭虚拟摄像头软件或连接真实摄像头后重试',
    );
  }

  throw defaultErr;
}

export default Webcam;
